import styled, { keyframes, css } from 'styled-components'

// Animation for slide down effect
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Main Container
export const Container = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  flex-direction: column;
  height: 100%;
`

// Header Section
export const Header = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  gap: ${({ theme }) => theme.spacing.small};
`

export const HeaderIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`

export const HeaderTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`

export const DealCount = styled.span`
  min-width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.label};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
  border-radius: ${({ theme }) => theme.borderRadius.round};
  padding: 0 ${({ theme }) => theme.spacing.small};
`

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.text.tertiary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

// Search Header Section
export const SearchHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const SearchInputWrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
`

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: flex;
  align-items: center;
  pointer-events: none;
`

export const SearchInput = styled.input`
  width: 100%;
  padding: 7px ${({ theme }) => theme.spacing.default} 7px 32px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-family: inherit;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const HeaderActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const HeaderActionButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

// Deals List Section
export const DealsList = styled.div`
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.large};
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.tertiary};
  }
`

// Deal Card Components
export const DealCard = styled.div<{
  $expanded?: boolean
  $isDeleting?: boolean
}>`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  transition: all ${({ theme }) => theme.transitions.fast};
  overflow: hidden;
  position: relative;

  &:hover {
    border-color: ${({ theme }) => theme.colors.border.primary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }

  ${({ $expanded, theme }) =>
    $expanded &&
    `
    border-color: ${theme.colors.primary};
    box-shadow: ${theme.shadows.md};
  `}

  ${({ $isDeleting }) =>
    $isDeleting &&
    `
    pointer-events: none;
  `}
`

export const DealHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.small};
`

export const DealStatusDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  flex-shrink: 0;
  margin-top: 6px;
  background: ${({ $color }) => $color};
`

export const DealMain = styled.div`
  flex: 1;
  min-width: 0;
`

export const DealTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.small};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
`

export const DealTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`

export const DealValue = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
`

export const DealMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const DealMetaSeparator = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const DealStage = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

export const DealCardActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xsmall};
  opacity: 0;
  transition: opacity ${({ theme }) => theme.transitions.fast};

  ${DealCard}:hover & {
    opacity: 1;
  }

  @media (max-width: 768px) {
    opacity: 1;
  }
`

export const DealCardActionButton = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.default};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.tertiary};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &.danger:hover {
    background: ${({ theme }) => theme.colors.dangerLight};
    color: ${({ theme }) => theme.colors.danger};
  }
`

export const ExpandIcon = styled.div<{ $expanded?: boolean }>`
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  transition: transform ${({ theme }) => theme.transitions.fast};
  flex-shrink: 0;
  margin-top: 4px;
  transform: ${({ $expanded }) =>
    $expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`

// Delete Confirmation Overlay
export const DeleteConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.default};
  animation: ${slideDown} 0.2s ease-out;
`

export const DeleteConfirmText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  text-align: center;
`

export const DeleteConfirmActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
`

export const DeleteConfirmButton = styled.button<{ $variant?: 'danger' }>`
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  ${({ $variant, theme }) =>
    $variant === 'danger' &&
    `
    background: ${theme.colors.danger};
    border-color: ${theme.colors.danger};
    color: white;

    &:hover {
      background: ${theme.colors.danger};
      opacity: 0.9;
    }
  `}
`

// Deal Details Section
export const DealDetails = styled.div<{ $expanded?: boolean }>`
  display: ${({ $expanded }) => ($expanded ? 'block' : 'none')};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  ${({ $expanded }) =>
    $expanded &&
    css`
      animation: ${slideDown} 0.2s ease-out;
    `}
`

export const DetailGrid = styled.div`
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.default};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.small};
`

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const DetailLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const DetailValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`

// Empty State Section
export const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.huge}
    ${({ theme }) => theme.spacing.large};
`

export const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto ${({ theme }) => theme.spacing.default};
  opacity: 0.2;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const EmptyTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const EmptyDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const EmptyActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  justify-content: center;
`

// Loading Container
export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: 400px;
`

// Modal Components
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ theme }) => theme.zIndex.modal};
`

export const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  width: 90%;
  max-width: 500px;
  max-height: 80%;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
`

export const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
`

export const ModalFooter = styled.div`
  padding: ${({ theme }) => theme.spacing.default}
    ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border.default};
  display: flex;
  gap: ${({ theme }) => theme.spacing.small};
  justify-content: flex-end;
`

// Form Components
export const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.default};
`

export const FormLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xsmall};
  display: block;
`

export const FormInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: inherit;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.background.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const FormSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: inherit;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.background.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }
`

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.small};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.default};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  font-family: inherit;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ theme }) => theme.colors.background.primary};
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

// Participant Components
export const ParticipantsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  margin-top: ${({ theme }) => theme.spacing.small};
`

export const ParticipantItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.default};
`

export const ParticipantAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.borderRadius.circle};
  background: ${({ theme }) => theme.colors.border.default};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const ParticipantInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`

export const ParticipantName = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

// No Results State
export const NoResultsState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xlarge}
    ${({ theme }) => theme.spacing.large};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const NoResultsIcon = styled.div`
  width: 32px;
  height: 32px;
  margin: 0 auto ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const NoResultsText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
`
